import {useEffect, useRef, useState, useContext} from "react";
import L from "leaflet";

import createWasteIconJS from "components/waste-icons/svgs/createWasteIconJS";
import getMarkerFontAwesomeJS from "components/waste-icons/svgs/getMarkerFontAwesomeJS";
import Loader from "components/loaders/Loader";
import DurationDisplay from "components/datestimes/DurationDisplay";
import AuthContext from "context/AuthContext";
import axiApi from "ApiCaller";
import { CARTO_TILE_URL, CARTO_TILE_OPTIONS } from 'core/mapsConfig';


const MapRouteCalculatorLeaflet = ({
    waypoints,
    setWaypoints,
    center,
    hasChanged,
    setHasChanged,
    geojson,
    setGeojson,
    duration,
    setDuration,
    highlightedPoint,
    tF,
    wasteType: routeWasteType,
}) => {
    const {panel} = useContext(AuthContext);
    const mapRef = useRef(null);
    const assetsLayerRef = useRef(L.layerGroup([]));
    const routeLayerRef = useRef(L.featureGroup([]));
    const highlightLayerRef = useRef(L.layerGroup([]));
    const markersRef = useRef({});
    const checkpoints = useRef([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (mapRef.current && !hasChanged) {
            addAssetsMap();
        }
    }, [waypoints]);

    useEffect(() => {
        // Crear mapa
        if (center) {
            if (!mapRef.current) {
                mapRef.current = L.map("route-static-map").setView(center, 13);
                L.tileLayer(CARTO_TILE_URL, CARTO_TILE_OPTIONS).addTo(mapRef.current);
                assetsLayerRef.current.addTo(mapRef.current);
                routeLayerRef.current.addTo(mapRef.current);
                highlightLayerRef.current.addTo(mapRef.current);
            }
            addAssetsMap();
            if (Object.keys(geojson).length > 0) {
                paintRoute(geojson);
            }
            setLoading(false);
        }
    }, [center, geojson]);

    useEffect(() => {
        if (hasChanged) {
            routeLayerRef.current.clearLayers();
        }
    }, [hasChanged]);

    const calculateRoute = async () => {
        try {
            const coords = checkpoints.current.map((point) => [point.lng, point.lat]);
            const resp = await axiApi.post("/api/distribution/route/travel/", {coordinates: coords, optimize: false});

            let times = 0;
            const unload = panel.unloading * 60;
            const items = [waypoints[0]];
            resp.data.geojson.legs.forEach((leg, idx) => {
                let index = idx + 1;
                let item_ = checkpoints.current[index];
                if (item_.is_asset) {
                    times += unload;
                    item_.step = leg.duration + unload;
                } else {
                    times += item_.waypoint_time * 60;
                    item_.step = leg.duration + item_.waypoint_time * 60;
                }

                times += leg.duration;
                items.push(item_);
                return false;
            });
            setWaypoints(items);
            setDuration(Math.ceil(times));
            setGeojson(resp.data.geojson);
            setHasChanged(false);
            paintRoute(resp.data.geojson);
        } catch (error) {
            console.error("Error al calcular la ruta:", error);
        }
    };
    const addAssetsMap = () => {
        assetsLayerRef.current.clearLayers();
        highlightLayerRef.current.clearLayers();
        markersRef.current = {};
        checkpoints.current = [...waypoints];
        checkpoints.current.forEach((point, index) => {
            if (point.lat == null || point.lng == null) return;
            let icon, name;
            if (point.is_asset) {
                icon = createWasteIconJS(point.waste_type || routeWasteType, point.filling || point.island_filling);
                name = "UID: " + point.uid;
            } else if (point?.mooch) {
                icon = createWasteIconJS("fl", 100);
                name = point.uid;
            } else {
                icon = getMarkerFontAwesomeJS(point.waypoint_icon);
                name = point.waypoint_name;
            }
            const marker = L.marker([point.lat, point.lng], {icon})
                .addTo(mapRef.current)
                .bindPopup(name);
            assetsLayerRef.current.addLayer(marker);
            const idKey = String(point.id ?? point.asset_id ?? point.waypoint_id ?? point.uid ?? index);
            markersRef.current[idKey] = marker;
        });
        assetsLayerRef.current.addTo(mapRef.current);
    };

    useEffect(() => {
        if (!mapRef.current) return;

        highlightLayerRef.current.clearLayers();

        if (!highlightedPoint) {
            // Restaurar opacidad de todos los marcadores
            Object.values(markersRef.current).forEach((m) => {
                try { const el = m.getElement(); if (el) el.style.opacity = "1"; } catch (e) {}
            });
            return;
        }

        const id = String(highlightedPoint);
        const activeMarker = markersRef.current[id];

        // Dim todos los marcadores que no son el activo
        Object.entries(markersRef.current).forEach(([key, m]) => {
            try { const el = m.getElement(); if (el) el.style.opacity = key === id ? "1" : "0.2"; } catch (e) {}
        });

        if (!activeMarker) return;

        const latlng = activeMarker.getLatLng();

        highlightLayerRef.current.addTo(mapRef.current);

        try { activeMarker.setZIndexOffset(1000); } catch (e) {}
        try { mapRef.current.panTo(latlng); } catch (e) {}
    }, [highlightedPoint, waypoints]);

    const paintRoute = (_geojson) => {
        routeLayerRef.current.clearLayers();
        if (!_geojson?.geometry?.coordinates?.length) return;
        _geojson.geometry.coordinates.forEach((steps) => {
            const coords = steps.map(([lng, lat]) => [lat, lng]);
            if (!coords.length) return;
            routeLayerRef.current.addLayer(L.polyline(coords, {color: "blue", opacity: 0.6}));
            const lastLatLng = coords.at(-1);
            if (lastLatLng)
                routeLayerRef.current.addLayer(
                    L.circle(lastLatLng, {
                        radius: 20,
                        color: "blue",
                        opacity: 0.3,
                        fillColor: "blue",
                        fillOpacity: 0.3,
                        weight: 1,
                    }),
                );
        });
        const bounds = routeLayerRef.current.getBounds();
        if (bounds.isValid()) mapRef.current.fitBounds(bounds);
    };

    return (
        <div style ={{height: "100%", width: "100%"}}>
            <div className="row g-3 mb-3">
                <div className="col-3 text-end">
                    <label htmlFor="description" className="col-form-label">
                        {tF("routes.duration")}
                    </label>
                </div>
                <div className="col-9">
                    <input
                        className="form-control disabled"
                        id="duration"
                        name="duration"
                        value={DurationDisplay({seconds: duration, format: "HH:mm"})}
                        readOnly
                        style={{width: "90px", backgroundColor: "#e9ecef", opacity: 1}}
                    />
                </div>
            </div>
            {hasChanged && waypoints.length > 1 ? (
                <div className="col-12 text-end">
                    <button type="button" className="btn btn-primary mb-3" onClick={calculateRoute}>
                        {tF("routes.calculate-route")}
                    </button>
                </div>
            ) : (
                <></>
            )}

            <div id="route-static-map" style={{height: "90%", width: "100%"}}>
                <Loader show={loading} />
            </div>
        </div>
    );
};

export default MapRouteCalculatorLeaflet;